/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-10-23T13:00:00+05:00
Intent: Lock editing for campaigns published on both the LuQta app and Web QR, with a message that says so.
*/

function flag(value: any): boolean | undefined {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return undefined;
}

/** The app visibility flag reports actual publication, unlike the submission preference
 * `is_visible_on_luqta_app` (which is set before the app approves/publishes a campaign).
 * `on_mobile` is the same fact under the publication-surface names. */
function appVisibility(item: any): boolean | undefined {
  const visible = flag(item?.is_visible_in_app);
  const onMobile = flag(item?.on_mobile);
  if (visible === true || onMobile === true) return true;
  return visible ?? onMobile;
}

function hasPublishTimestamp(item: any): boolean {
  return Boolean(item?.published_at ?? item?.publishedAt);
}

/** Approved before it started ("approved") or after ("ongoing"/"active"); the
 * approval itself may only be kept in `review_status` once the lifecycle moves on. */
function isApproved(status: string, reviewStatus: string): boolean {
  return ['approved', 'ongoing', 'active'].includes(status) || reviewStatus === 'approved';
}

/** Existing backend `web_approved` is an explicit Web QR approval; a campaign the
 * publication surfaces report as live on Web and not in the app is the same thing,
 * as is an approved campaign published with app visibility explicitly OFF. Missing
 * app visibility is intentionally NOT interpreted as Web QR-only. */
export function isWebQrOnlyApproved(item: any): boolean {
  const status = String(item?.status ?? '').toLowerCase();
  const reviewStatus = String(item?.review_status ?? '').toLowerCase();
  const visibleInApp = appVisibility(item);
  if (visibleInApp === true) return false;
  if (status === 'web_approved' || reviewStatus === 'web_approved') return true;
  if (flag(item?.on_web) === true) return true;
  return isApproved(status, reviewStatus) && visibleInApp === false && hasPublishTimestamp(item);
}

/** The status a published Web QR-only campaign is saved back with, so an edit does not
 * send it to "pending" and take it off the web. Lifecycle values ("ongoing") are not
 * writable statuses, so the approval itself is used. Null for every other campaign. */
export function webQrApprovalStatus(item: any): string | null {
  if (!isWebQrOnlyApproved(item)) return null;
  const status = String(item?.status ?? '').toLowerCase();
  const reviewStatus = String(item?.review_status ?? '').toLowerCase();
  return [status, reviewStatus].find((s) => s === 'web_approved' || s === 'approved') ?? 'approved';
}

/** A running campaign that is NOT in the LuQta app, labelled by the business's own
 * surfaces it runs on. null when it is in the app (plain "Ongoing") or the channels
 * are unknown. */
export function offAppOngoingStatus(item: any): 'webqr_ongoing' | 'sdk_ongoing' | 'sdk_webqr_ongoing' | null {
  if (appVisibility(item) !== false) return null;
  const sdk = flag(item?.on_sdk) === true;
  const web = flag(item?.on_web) === true;
  if (sdk && web) return 'sdk_webqr_ongoing';
  if (sdk) return 'sdk_ongoing';
  if (web) return 'webqr_ongoing';
  return null;
}

/** An approved, not-yet-started SDK-only campaign; Web QR keeps its existing `web_approved`. */
export function isSdkOnlyApproved(item: any): boolean {
  return appVisibility(item) === false && flag(item?.on_sdk) === true && flag(item?.on_web) !== true;
}

/** Live in the LuQta app and on Web QR at the same time. */
export function isPublishedOnAppAndWebQr(item: any): boolean {
  const status = String(item?.status ?? '').toLowerCase();
  const reviewStatus = String(item?.review_status ?? '').toLowerCase();
  const onWeb = flag(item?.on_web) === true || status === 'web_approved' || reviewStatus === 'web_approved';
  return appVisibility(item) === true && onWeb;
}

/** The translation key saying why a campaign cannot be edited. */
export function cannotEditMessageKey(item: any): string {
  return isPublishedOnAppAndWebQr(item)
    ? 'contestDetails.cannotEditAppAndWebQr'
    : 'contestDetails.cannotEditApprovedCompleted';
}

/** Approved and ongoing campaigns (app or Web QR) can be revised until their end
 * date; finished campaigns, and ones published on both the app and Web QR, keep the lock. */
export function canEditCampaign(item: any): boolean {
  const status = String(item?.status ?? '').toLowerCase();
  if (['complete', 'completed', 'waiting_prize'].includes(status)) return false;
  if (isPublishedOnAppAndWebQr(item)) return false;
  // Approved campaigns past their end date are awaiting prizes, not editable.
  const end = item?.end_date ? new Date(item.end_date).getTime() : NaN;
  const ended = Number.isFinite(end) && Date.now() >= end;
  // A Web QR publication is the business's own: editable until the campaign ends,
  // whatever lifecycle status the backend reports for it.
  if (isWebQrOnlyApproved(item)) return !ended;
  // Ongoing lock disabled: live app campaigns must stay editable while running.
  // if (status === 'ongoing' && !isWebQrOnlyApproved(item)) return false;
  if (status === 'approved' || status === 'web_approved' || status === 'ongoing') {
    // App-approved lock disabled for the same reason ("ongoing" is approved + started).
    // if (!isWebQrOnlyApproved(item)) return false;
    return !ended;
  }
  // A pending/draft campaign can still be edited unless it is already live in the app.
  return appVisibility(item) !== true;
}
